import React, { useEffect, useState } from 'react';
import CloseButton from '../components/CloseButton';

export default function AngelicarePanel({ onClose = null }) {
  const ANGELICARE_URL = 'https://angelicare.zenit.poznan.pl/overall';
  const ANGELICARE_STRINGS_URL = 'https://angelicare.zenit.poznan.pl/beliefs';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stringsLoading, setStringsLoading] = useState(true);
  const [stringsError, setStringsError] = useState(null);
  const [stringItems, setStringItems] = useState([]);
  const [stats, setStats] = useState({
    senses: 0,
    facts: 0,
    beliefs: 0,
    targets: 0,
    cycles: 0,
    errors: 0,
    age: 0
  });

  const parseOverallText = (text) => {
    const parsed = {};
    text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        const [key, value] = line.split('=');
        if (!key || value === undefined) {
          return;
        }
        const parsedValue = parseInt(value, 10);
        parsed[key.trim()] = Number.isFinite(parsedValue) ? parsedValue : 0;
      });
    return parsed;
  };

  const formatTimespan = (millis) => {
    const safeMillis = Number.isFinite(millis) ? Math.max(0, millis) : 0;
    const totalSeconds = Math.floor(safeMillis / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const parseBeliefItem = (item) => {
    const trimmed = String(item || '').trim();
    const noBeliefPrefix = trimmed.replace(/^belief\s+/i, '');

    const match = noBeliefPrefix.match(/^(.*?)(?:\s*\((.*)\))?$/);
    const mainText = (match?.[1] || noBeliefPrefix).trim();
    const metaText = (match?.[2] || '').trim();

    const confidenceMatch = metaText.match(/confidence\s+([0-9]+(?:\.[0-9]+)?)%/i);
    const confidence = confidenceMatch ? parseFloat(confidenceMatch[1]) : -1;

    return {
      mainText,
      metaText,
      confidence
    };
  };

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(ANGELICARE_URL);
        if (!response.ok) {
          throw new Error('Request failed');
        }
        const textData = await response.text();
        const data = parseOverallText(textData);
        if (!cancelled) {
          setStats({
            senses: Number.isFinite(data?.senses) ? data.senses : 4,
            facts: Number.isFinite(data?.facts) ? data.facts : 4,
            beliefs: Number.isFinite(data?.beliefs) ? data.beliefs : 4,
            targets: Number.isFinite(data?.targets) ? data.targets : 2,
            cycles: Number.isFinite(data?.cycles) ? data.cycles : 5,
            errors: Number.isFinite(data?.errors) ? data.errors : 0,
            age: Number.isFinite(data?.age) ? data.age : 78336
          });
        }
      } catch (e) {
        if (!cancelled) {
          setError('Could not load data from endpoint.');
          setStats({
            senses: 0,
            facts: 0,
            beliefs: 0,
            targets: 0,
            cycles: 0,
            errors: 0,
            age: 0
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      cancelled = true;
    };
  }, []);

  const parsedStringItems = stringItems
    .map(parseBeliefItem)
    .sort((a, b) => b.confidence - a.confidence);

  useEffect(() => {
    let cancelled = false;

    async function loadStrings() {
      setStringsLoading(true);
      setStringsError(null);
      try {
        const response = await fetch(ANGELICARE_STRINGS_URL);
        if (!response.ok) {
          throw new Error('Request failed');
        }
        const textData = await response.text();
        const values = textData
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean);
        if (!cancelled) {
          setStringItems(values);
        }
      } catch (e) {
        if (!cancelled) {
          setStringsError('Could not load data.');
          setStringItems(['string1', 'string2 with space', 'string3 something']);
        }
      } finally {
        if (!cancelled) {
          setStringsLoading(false);
        }
      }
    }

    loadStrings();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div style={{
      width: '95vw',
      height: '95vh',
      maxWidth: '1000px',
      maxHeight: '700px',
      background: '#fff',
      borderRadius: 16,
      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{
        background: '#333',
        color: '#fff',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderRadius: '16px 16px 0 0'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <span style={{ fontSize: '1.5rem' }}>💗</span>
          <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>Angelicare</span>
        </div>
        <CloseButton
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}
        />
      </div>

      <div style={{
        width: '100%',
        height: 'calc(100% - 60px)',
        background: '#f5f5f5',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 24,
        boxSizing: 'border-box',
        overflowY: 'auto'
      }}>
        <div style={{ width: '100%', maxWidth: 700 }}>
          <div style={{
            width: '100%',
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
          }}>
          {loading ? (
            <div style={{ color: '#666', fontSize: '1.05rem' }}>Loading Angelicare data...</div>
          ) : (
            <>
              {error && (
                <div style={{
                  marginBottom: 16,
                  color: '#9c2f2f',
                  background: '#fdeeee',
                  padding: '10px 12px',
                  borderRadius: 8
                }}>
                  {error}
                </div>
              )}

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 16
              }}>
                <div style={{
                  border: '1px solid #ececec',
                  borderRadius: 10,
                  padding: 16,
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#333' }}>{stats.senses}</div>
                  <div style={{ fontSize: '0.75rem', color: '#777', marginTop: 6 }}>senses</div>
                </div>
                <div style={{
                  border: '1px solid #ececec',
                  borderRadius: 10,
                  padding: 16,
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#333' }}>{stats.facts}</div>
                  <div style={{ fontSize: '0.75rem', color: '#777', marginTop: 6 }}>facts</div>
                </div>
                <div style={{
                  border: '1px solid #ececec',
                  borderRadius: 10,
                  padding: 16,
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#333' }}>{stats.beliefs}</div>
                  <div style={{ fontSize: '0.75rem', color: '#777', marginTop: 6 }}>beliefs</div>
                </div>
                <div style={{
                  border: '1px solid #ececec',
                  borderRadius: 10,
                  padding: 16,
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#333' }}>{stats.targets}</div>
                  <div style={{ fontSize: '0.75rem', color: '#777', marginTop: 6 }}>targets</div>
                </div>
                <div style={{
                  border: '1px solid #ececec',
                  borderRadius: 10,
                  padding: 16,
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#333' }}>{stats.cycles}</div>
                  <div style={{ fontSize: '0.75rem', color: '#777', marginTop: 6 }}>cycles</div>
                </div>
                <div style={{
                  border: '1px solid #ececec',
                  borderRadius: 10,
                  padding: 16,
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#333' }}>{stats.errors}</div>
                  <div style={{ fontSize: '0.75rem', color: '#777', marginTop: 6 }}>errors</div>
                </div>
                <div style={{
                  border: '1px solid #ececec',
                  borderRadius: 10,
                  padding: 16,
                  textAlign: 'center',
                  gridColumn: 'span 2'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#333' }}>{formatTimespan(stats.age)}</div>
                  <div style={{ fontSize: '0.75rem', color: '#777', marginTop: 6 }}>age</div>
                </div>
              </div>
            </>
          )}
          </div>

          <div style={{
            width: '100%',
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            marginTop: 16
          }}>
            {stringsLoading ? (
              <div style={{ color: '#666', fontSize: '1.05rem' }}>Loading string list...</div>
            ) : (
              <>
                {stringsError && (
                  <div style={{
                    marginBottom: 16,
                    color: '#9c2f2f',
                    background: '#fdeeee',
                    padding: '10px 12px',
                    borderRadius: 8
                  }}>
                    {stringsError}
                  </div>
                )}

                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {parsedStringItems.map((item, index) => (
                    <li key={`${item.mainText}-${item.metaText}-${index}`} style={{ marginBottom: 8, color: '#333' }}>
                      <div>{item.mainText}</div>
                      {item.metaText && (
                        <div style={{ fontSize: '0.78rem', color: '#666', marginTop: 2 }}>
                          {item.metaText}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}